// @flow

import React, { Component, Fragment } from "react";
import { isEqual, noop, isFunction, get } from "lodash";
import { CSSTransition } from "react-transition-group";
import type { RaceCardRunner, RaceProgram } from "@tvg/types/Race";
import type { PastPerformanceRace as PastPerformanceRaceType } from "@fdr/types/PastPerformance";
import type { BinaryFn, NullaryFn, UnaryFn } from "@tvg/types/Functional";
import { sportsBook } from "@fdr/static-ui/Icons/icons";
import { RaceLogMask } from "@fdr/static-ui/Masks";

import PastPerformanceRace from "../../_molecules/PastPerformanceRace";
import PastPerformanceList from "../../_organisms/PastPerformanceList";
import Toggle from "../../_atoms/Toggle";

import {
  ListHolder,
  ListWrapper,
  MatchHolder,
  ListHeader
} from "./styled-components";
import FullPageMessage from "../../_molecules/FullPageMessage";
import PastPerformanceRunnerSelector from "../../_molecules/PastPerformanceRunnerSelector";

type Props = {
  originRace: string,
  races: PastPerformanceRaceType[],
  selectedRace: number,
  totalRaces: number,
  onRace: UnaryFn<number, void>,
  onMoreRaces: ?NullaryFn<void>,
  onMatchToggle: ?NullaryFn<void>,
  isMatchConditionsOn: boolean,
  isLoading: boolean,
  emptyMessage: string,
  paddingless: boolean,
  borderless: boolean,
  isMobile: boolean,
  currentRace: ?RaceProgram,
  activeRunner: string,
  runners: RaceCardRunner[],
  onRunnerClick: BinaryFn<string, string, void>,
  onRaceClose: NullaryFn<void>,
  isLoadingLog: boolean
};

export default class InlinePastPerformance extends Component<Props> {
  static defaultProps = {
    originRace: "",
    races: [],
    selectedRace: -1,
    totalRaces: 0,
    onRace: noop,
    onMoreRaces: null,
    onMatchToggle: null,
    isMatchConditionsOn: false,
    isLoading: false,
    emptyMessage: "No replays are available for this horse.",
    paddingless: false,
    borderless: false,
    isMobile: false,
    currentRace: null,
    activeRunner: "",
    runners: [],
    onRunnerClick: noop,
    onRaceClose: noop,
    isLoadingLog: true
  };

  shouldComponentUpdate(nextProps: Props) {
    return (
      this.props.activeRunner !== nextProps.activeRunner ||
      this.props.isMatchConditionsOn !== nextProps.isMatchConditionsOn ||
      this.props.totalRaces !== nextProps.totalRaces ||
      this.props.selectedRace !== nextProps.selectedRace ||
      this.props.races.length !== nextProps.races.length ||
      this.props.isLoading !== nextProps.isLoading ||
      this.props.isLoadingLog !== nextProps.isLoadingLog ||
      !isEqual(this.props.runners, nextProps.runners) ||
      !isEqual(this.props.onMoreRaces, nextProps.onMoreRaces) ||
      !isEqual(this.props.currentRace, nextProps.currentRace) ||
      !isEqual(this.props.races, nextProps.races)
    );
  }

  renderEmpty = () =>
    !this.props.isMatchConditionsOn ? (
      <FullPageMessage
        icon={sportsBook}
        iconSize={180}
        textTitle="No Previous Races Available"
        qaLabel="no-previous-races"
      />
    ) : (
      <FullPageMessage
        icon={sportsBook}
        iconSize={180}
        textBody={"There are no previous races that match Today's Conditions"}
        qaLabel="no-previous-races"
      />
    );

  renderList = (
    hasMoreButtonPresent: boolean = isFunction(this.props.onMoreRaces)
  ) => (
    <Fragment>
      {get(this.props, "currentRace.status.code") !== "RO" &&
        get(this.props, "currentRace.status.code") !== "SK" && (
          <Fragment>
            <ListHeader>current race</ListHeader>
            <PastPerformanceRace
              race={null}
              currentRace={this.props.currentRace}
            />
          </Fragment>
        )}
      <ListHeader hasBorderTop>Full race log</ListHeader>
      <ListWrapper
        borderless={this.props.borderless}
        data-qa-label={hasMoreButtonPresent ? "ipp-shortlist" : "ipp-fulllist"}
      >
        {this.props.isLoadingLog ? (
          <RaceLogMask />
        ) : (
          <PastPerformanceList
            races={this.props.races}
            selectedRace={this.props.selectedRace}
            onRace={this.props.onRace}
            hasMoreButtonPresent={hasMoreButtonPresent}
            isMobile={this.props.isMobile}
            isLoading={this.props.isLoading}
          />
        )}
      </ListWrapper>
    </Fragment>
  );

  renderMatchToggle = () =>
    isFunction(this.props.onMatchToggle) && (
      <MatchHolder>
        <span>Only show similar conditions to today</span>
        <Toggle
          on={this.props.isMatchConditionsOn}
          // $FlowFixMe
          onClick={this.props.onMatchToggle}
          qaLabel="ipp-match-condition-button"
        />
      </MatchHolder>
    );

  render() {
    return (
      <Fragment>
        <CSSTransition
          in={!this.props.isLoading}
          classNames="expand-inside"
          timeout={200}
          unmountOnExit
        >
          <ListHolder
            paddingBottom={
              !this.props.paddingless && !isFunction(this.props.onMoreRaces)
            }
            paddingless={this.props.paddingless}
            className="expandable"
          >
            <PastPerformanceRunnerSelector
              raceTypeCode={get(this.props, "currentRace.type.code", "T")}
              activeRunner={this.props.activeRunner}
              runners={this.props.runners}
              onRunnerClick={this.props.onRunnerClick}
            />
            {this.renderMatchToggle()}
            {this.props.races.length > 0 || this.props.isLoadingLog
              ? this.renderList()
              : this.renderEmpty()}
          </ListHolder>
        </CSSTransition>
      </Fragment>
    );
  }
}
